export type WorkoutVoiceStatus = 'idle' | 'requesting' | 'recording' | 'transcribing' | 'error';
export interface WorkoutVoiceCapture {
  start(): Promise<void>;
  stop(): Promise<FormData>;
  dispose(): Promise<void>;
}

/** One recording / transcription transaction. Late callbacks never cross a cancelled session. */
export class WorkoutVoiceSession {
  status: WorkoutVoiceStatus = 'idle';
  private generation = 0;
  private capture: WorkoutVoiceCapture | null = null;
  private timer: ReturnType<typeof setInterval> | null = null;
  constructor(private readonly options: {
    createCapture(): WorkoutVoiceCapture;
    transcribe(data: FormData): Promise<string>;
    onStatus(status: WorkoutVoiceStatus): void;
    onTranscript(text: string): void;
    onError(error: unknown): void;
    onElapsed(seconds: number): void;
  }) {}
  private state(status: WorkoutVoiceStatus) { this.status = status; this.options.onStatus(status); }
  private clearTimer() { if (this.timer) clearInterval(this.timer); this.timer = null; }
  async cancel(notify = true) {
    ++this.generation;
    this.clearTimer();
    const old = this.capture; this.capture = null;
    this.status = 'idle';
    if (notify) { this.options.onStatus('idle'); this.options.onElapsed(0); }
    await old?.dispose().catch(() => undefined);
  }
  async start() {
    if (this.capture || this.status === 'requesting' || this.status === 'transcribing') return;
    const generation = ++this.generation;
    this.state('requesting'); this.options.onElapsed(0);
    let capture: WorkoutVoiceCapture | null = null;
    try {
      capture = this.options.createCapture(); this.capture = capture;
      await capture.start();
      if (generation !== this.generation) { await capture.dispose(); return; }
      this.state('recording');
      const since = Date.now();
      this.timer = setInterval(() => {
        const elapsed = Math.min(60, Math.floor((Date.now() - since) / 1000));
        this.options.onElapsed(elapsed);
        if (elapsed >= 60) void this.stop();
      }, 250);
    } catch (error) {
      await capture?.dispose().catch(() => undefined);
      if (generation !== this.generation) return;
      this.capture = null; this.state('error'); this.options.onError(error);
    }
  }
  async stop() {
    const capture = this.capture;
    if (!capture || this.status !== 'recording') return;
    const generation = this.generation;
    this.clearTimer(); this.state('transcribing');
    try {
      const data = await capture.stop();
      if (generation !== this.generation) return;
      const transcript = (await this.options.transcribe(data)).trim();
      if (generation !== this.generation) return;
      if (!transcript) throw new Error('empty');
      this.options.onTranscript(transcript);
      this.state('idle');
    } catch (error) {
      if (generation === this.generation) { this.state('error'); this.options.onError(error); }
    } finally {
      await capture.dispose().catch(() => undefined);
      if (this.capture === capture) this.capture = null;
    }
  }
}
